import React from 'react';
import { useCustomerView } from '@/hooks/useCustomerView';
import { useAuth } from '@/context/AuthContext';

/**
 * AdminFloatingChromeGate — wraps every admin-only floating widget
 * (task button, bells, image-upload FAB, impersonation pill, etc.)
 * so they all disappear together the instant "View as Customer" is
 * toggled on, and re-appear when admin exits the customer view.
 *
 * v2 (2026-06-22): also hard-hides for REAL customer logins. A regular
 * customer browsing jaiparasapp.in was seeing the floating "Cancel
 * Print" dialog because this wrapper used to only check the
 * impersonation flag and assume every wrapped component would do its
 * own role check. Now the wrapper itself enforces admin-or-staff —
 * defense in depth, no single forgotten role check can cause another
 * customer-facing leak.
 *
 * v3 (2026-02-17): fixed staff-only login rendering bug. The previous
 * condition `user && (... || staffUser?.staff_id)` required a customer
 * `user` object to be present, so staff who logged in via /staff-login
 * (with no linked customer session) had `user=null` and the whole chrome
 * gate collapsed. Now the staff-token branch is independent of `user`.
 *
 * v4 (2026-08-02): office staff (Kumkum, Dharmendra Yadav, Yadav, etc.)
 * who log in as regular CUSTOMERS but are whitelisted in
 * task_manager_panel.PANEL_USERS were being blocked by this gate
 * because they're neither admin nor is_staff — even though they're
 * expected to see the Task Panel bell, TallyPending, weighment/photo
 * upload, and WhatsApp shortcut. Backend `/auth/me` now returns
 * `has_task_panel_access: true` for anyone in PANEL_MOBILES; the gate
 * lets them through. Each individual floating widget still does its
 * OWN /access role check downstream, so admin-only widgets stay
 * hidden from panel-authorized office staff (defence in depth).
 */
const AdminFloatingChromeGate = ({ children }) => {
  const { isCustomerView } = useCustomerView();
  const { user } = useAuth();
  let staffUser = null;
  try {
    // Staff logins store under sessionStorage['staff_user'] (see AuthPage.js,
    // StaffLoginPage.js, Navbar.js). Fall back to localStorage for any legacy
    // callers that may still be writing there.
    const raw = sessionStorage.getItem('staff_user') || localStorage.getItem('staff_user');
    if (raw) staffUser = JSON.parse(raw);
  } catch (_) { staffUser = null; }
  const isAdmin = Boolean(user && (user.is_admin || user._is_admin || user.is_staff));
  const isStaff = Boolean(staffUser?.staff_id);
  const hasPanelAccess = Boolean(user?.has_task_panel_access);
  if (isCustomerView) return null;
  if (!isAdmin && !isStaff && !hasPanelAccess) return null;
  return <>{children}</>;
};

export default AdminFloatingChromeGate;
